import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { HistoryProvider } from './context/HistoryContext';
import { DiagnosticProvider } from './context/DiagnosticContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AdminRoute } from './components/AdminRoute';
import { MaintenanceGuard } from './components/MaintenanceGuard';
import { DiagnosticRouteGuard } from './components/DiagnosticRouteGuard';
import { Home } from './pages/Home';
import { LoginPage } from './pages/LoginPage';
import { Dashboard } from './pages/Dashboard';
import { DiagnosticFlow } from './pages/DiagnosticFlow';
import { ReportPage } from './pages/ReportPage';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { TermsOfService } from './pages/TermsOfService';
import { RefundPolicy } from './pages/RefundPolicy';
import { ContactSupport } from './pages/ContactSupport';
import { NotFound } from './pages/NotFound';
import { MaintenancePage } from './pages/MaintenancePage';
import { AdminApp } from './pages/admin/AdminApp';
import { PricingPage } from './pages/PricingPage';
import { PaymentSuccess } from './pages/PaymentSuccess';
import { PaymentCancelled } from './pages/PaymentCancelled';
import { ScrollToTop } from './components/ScrollToTop';
import { ManualDiagnostic } from './pages/diagnostic/advanced/ManualDiagnostic';
import { VerifiedPage } from './pages/VerifiedPage';
import { GuidePage } from './pages/GuidePage';
import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { trackEvent } from './services/firebase';
import './styles/global.css';

function AuthActionRedirect() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const mode = queryParams.get('mode');
  
  if (mode === 'resetPassword' || mode === 'verifyEmail' || mode === 'recoverEmail') {
    return <Navigate to={`/login${location.search}`} replace />;
  }
  
  return null;
}

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackEvent('page_view', {
      page_path: location.pathname,
      page_search: location.search,
      page_title: document.title,
      page_location: window.location.href
    });
  }, [location]);

  return null;
}

function App() {
  return (
    <AuthProvider>
      <HistoryProvider>
        <Router>
          <AnalyticsTracker />
          <DiagnosticProvider>
            <ScrollToTop />
            <MaintenanceGuard>
              <Routes>
                <Route path="/" element={<><AuthActionRedirect /><Home /></>} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/payment-success" element={<PaymentSuccess />} />
                <Route path="/payment-cancelled" element={<PaymentCancelled />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/refund-policy" element={<RefundPolicy />} />
                <Route path="/contact-support" element={<ContactSupport />} />
                <Route path="/auth/verified" element={<VerifiedPage />} />
                <Route path="/guide" element={<GuidePage />} />
                
                {/* Legacy routes */}
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/refund" element={<RefundPolicy />} />
                <Route path="/contact" element={<ContactSupport />} />
                <Route path="/maintenance" element={<MaintenancePage />} />
                
                {/* Protected Routes */}
                <Route 
                  path="/dashboard" 
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  } 
                />
                
                {/* Diagnostic Flow */}
                <Route path="/diagnostic" element={<ProtectedRoute><DiagnosticFlow /></ProtectedRoute>} />
                <Route path="/diagnostic/advanced" element={<ProtectedRoute><ManualDiagnostic /></ProtectedRoute>} />
                
                {/* Backwards compatibility */}
                <Route path="/diagnose" element={<Navigate to="/diagnostic" replace />} />
                
                <Route 
                  path="/report" 
                  element={
                    <ProtectedRoute>
                      <ReportPage />
                    </ProtectedRoute>
                  } 
                />

                {/* Admin Routes */}
                <Route 
                  path="/admin/*" 
                  element={
                    <AdminRoute>
                      <AdminApp />
                    </AdminRoute>
                  } 
                />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </MaintenanceGuard>
          </DiagnosticProvider>
        </Router>
      </HistoryProvider>
    </AuthProvider>
  );
}

export default App;

